import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  LayoutDashboard, FileQuestion, ClipboardList, BarChart3,
  Shield, LogOut, Users, Plus, Trash2, Activity, MessageSquare, Mail, Calendar
} from "lucide-react";

const OrganiserManagement = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [organisers, setOrganisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState({ full_name: "", email: "", password: "" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchOrganisers();
  }, []);

  const fetchOrganisers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/organisers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganisers(data);
    } catch (error) {
      toast.error("Failed to fetch organisers");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.full_name || !formData.email || !formData.password) {
      toast.error("All fields are required");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/organisers`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Organiser ${formData.email} created`);
      setShowCreateDialog(false);
      setFormData({ full_name: "", email: "", password: "" });
      fetchOrganisers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create organiser");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, email) => {
    if (!window.confirm(`Delete organiser ${email}?`)) return;
    try {
      await axios.delete(`${API}/admin/organisers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Organiser deleted");
      fetchOrganisers();
    } catch (error) {
      toast.error("Failed to delete organiser");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/auth");
  };

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
    { icon: FileQuestion, label: "Question Bank", path: "/admin/questions" },
    { icon: ClipboardList, label: "Exams", path: "/admin/exams" },
    { icon: BarChart3, label: "Results", path: "/admin/results" },
    { icon: Users, label: "Organisers", path: "/admin/organisers", active: true },
    { icon: MessageSquare, label: "Feedback", path: "/admin/feedback" },
    { icon: Activity, label: "IP Logs", path: "/admin/logs" }
  ];

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="organiser-management">
      <aside className="sidebar">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-blue-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>edSolutions360</span>
              <p className="text-xs text-slate-500">Examination Portal</p>
            </div>
          </div>
          <nav className="space-y-2">
            {navItems.map((item) => (
              <div key={item.path} className={`nav-item ${item.active ? 'active' : ''}`} onClick={() => navigate(item.path)} data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}>
                <item.icon className="w-5 h-5" /><span>{item.label}</span>
              </div>
            ))}
          </nav>
        </div>
        <div className="mt-auto p-6 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center"><Users className="w-5 h-5 text-indigo-600" /></div>
            <div>
              <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout} data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" />Logout
          </Button>
        </div>
      </aside>

      <main className="main-content p-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Organiser Management</h1>
              <p className="text-slate-600 mt-1">Create and manage organiser accounts</p>
            </div>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => setShowCreateDialog(true)}
              data-testid="create-organiser-btn"
            >
              <Plus className="w-4 h-4 mr-2" />Create Organiser
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
          ) : organisers.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-600">No organisers yet</h3>
                <p className="text-slate-500 mt-2">Create an organiser account to get started.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {organisers.map((org) => (
                <Card key={org.id} className="card-hover" data-testid={`organiser-card-${org.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                          <Users className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">{org.full_name}</h4>
                          <p className="text-sm text-slate-500 flex items-center gap-1">
                            <Mail className="w-3 h-3" />{org.email}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(org.id, org.email)}
                        data-testid={`delete-organiser-${org.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-500">
                      <Calendar className="w-3 h-3" />
                      Created: {new Date(org.created_at).toLocaleDateString()}
                    </div>
                    <Badge variant="secondary" className="mt-2 text-xs">Organiser</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Create Organiser Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Organiser Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Full Name</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="John Doe"
                data-testid="organiser-name-input"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="organiser@example.com"
                data-testid="organiser-email-input"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Minimum 6 characters"
                data-testid="organiser-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={creating}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-create-organiser-btn"
            >
              {creating ? "Creating..." : "Create Organiser"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganiserManagement;
